import React, { useCallback, useEffect, useState, SVGProps, ReactElement } from 'react'
import { ISpeedtest } from '.'
import { checkEndpoints, latencytest, sessionDownload, sessionUpload, speedtest } from './utils'
import { Speedometer, SpeedometerTitle, SpeedometerDisclaimer } from './components/Speedometer'
import { ISpeedtestDataSource, SPEED_TEST_STATUS, TestStatus, SpeedtestValues } from './Speedtest.types'
import useMobileDisplay from '../../hooks/useMobileDisplay'

// ASSETS
import Refresh from '@tdcerhverv/parrotfish/dist/icons/Icons/Refresh.svg'
import STBackground from '../static/speedtest-background.svg'

//MUI Components
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import useMediaQuery from '@mui/material/useMediaQuery'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import { Theme, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'

const PingElement = ({ pingValue }: { pingValue: number }) => {
  const styles = {
    pingContainer: ({ breakpoints, palette }: Theme) => ({
      textAlign: 'center',
      marginBottom: '1rem',
      marginTop: '-60px',
      borderRadius: '100px',
      border: `2px solid ${palette.primary.dark}`,
      padding: '.5rem',
      display: 'inline-flex',
      width: '50px',
      height: '50px',
      justifyContent: 'center',
      alignItems: 'center',
      [breakpoints.down('md')]: {
        marginTop: '-16px',
        marginBottom: '-5px',
      },
    }),
  }
  return (
    <Tooltip
      title={`En ping på ${pingValue}ms viser hvor langt tid det tog at sende en data mellem to værter. En ping mindre end 30ms er acceptabel.`}
    >
      <Box sx={styles.pingContainer}>
        <Typography>
          {`${pingValue}`}
          <span>ms</span>
        </Typography>
      </Box>
    </Tooltip>
  )
}

const defaultDataSource: ISpeedtestDataSource = {
  concurrency: 10,
  pingServerEndpoint: 'wss://linespeed1.nms.tele.dk:8080/ws',
  targetServerCollection: ['wss://linespeed1.nms.tele.dk:8080/ws', 'wss://linespeed3.nms.tele.dk:8080/ws'],
}

const SpeedtestBackground: (props: SVGProps<SVGElement>) => ReactElement = STBackground

const Speedtest = ({
  activateSpeedtest = false,
  getSpeedtestStatus,
  getSpeedtestValues,
  downloadLabel,
  uploadLabel,
  maxSpeed = 500,
  dataSource,
  hasVisuals,
  trackingId,
}: ISpeedtest) => {
  const isMobile = useMobileDisplay('md')
  const { palette, breakpoints } = useTheme()
  const isMdUp = useMediaQuery(breakpoints.up('md'))
  const isMdDown = useMediaQuery(breakpoints.down('md'))
  const [isAnimating, setIsAnimating] = useState<boolean>(false)
  const buttonText = hasVisuals ? 'START TEST' : 'Start testen'
  const [testRunning, setTestRunning] = useState<boolean>(false)
  const [isReady, setIsReady] = useState(false)

  const [speedtestStatus, setSpeedtestStatus] = useState<TestStatus>('INITIAL')
  const [prevTestStatus, setPrevTestStatus] = useState<TestStatus>(speedtestStatus)
  const [state, setState] = useState<SpeedtestValues>({
    [SPEED_TEST_STATUS.IPINFO]: null,
    [SPEED_TEST_STATUS.DOWNLOAD]: 0,
    [SPEED_TEST_STATUS.UPLOAD]: 0,
    [SPEED_TEST_STATUS.PING]: 0,
  })

  const [datapoints, setDatapoints] = useState<Record<string, number[]>>({
    [SPEED_TEST_STATUS.DOWNLOAD]: [],
    [SPEED_TEST_STATUS.UPLOAD]: [],
  })

  const { concurrency, pingServerEndpoint, targetServerCollection } = dataSource ? dataSource : defaultDataSource

  const resetSpeeds = useCallback(() => {
    setState({
      ...state,
      [SPEED_TEST_STATUS.DOWNLOAD]: 0,
      [SPEED_TEST_STATUS.UPLOAD]: 0,
      [SPEED_TEST_STATUS.PING]: 0,
    })

    setDatapoints({
      [SPEED_TEST_STATUS.DOWNLOAD]: [],
      [SPEED_TEST_STATUS.UPLOAD]: [],
    })
  }, [state])

  useEffect(() => {
    setIsAnimating(false)
    getAvailableTargets()
  }, [])

  useEffect(() => {
    if (getSpeedtestStatus) {
      getSpeedtestStatus(speedtestStatus)
    }
    if (getSpeedtestValues) {
      getSpeedtestValues(state)
    }
  }, [speedtestStatus, state])

  const getAvailableTargets = async () => {
    const availableServers = await checkEndpoints(defaultDataSource.targetServerCollection)
    defaultDataSource.targetServerCollection = availableServers
    setIsReady(true)
  }

  const startSpeedtest = () => {
    if (speedtestStatus === SPEED_TEST_STATUS.COMPLETE) {
      resetSpeeds()
    }
    setTestRunning(true)
    setIsAnimating(true)
    setSpeedtestStatus(SPEED_TEST_STATUS.DOWNLOAD)
  }

  useEffect(() => {
    if (activateSpeedtest) {
      startSpeedtest()
    }
  }, [activateSpeedtest])

  const normalizeResult = useCallback(() => {
    if (speedtestStatus === SPEED_TEST_STATUS.DOWNLOAD || speedtestStatus === SPEED_TEST_STATUS.UPLOAD) {
      const dp = datapoints[speedtestStatus]
      // Average speed is calculated out of 3 highest datapoints
      const highestResults = []
      const average = (array: number[]) => array.reduce((a, b) => a + b) / array.length
      for (let i = 0; i < 3; i++) {
        const highestDataPoint = Math.max(...dp)
        dp.splice(dp.indexOf(highestDataPoint), 1)
        highestResults.push(highestDataPoint)
      }
      setState({
        ...state,
        [speedtestStatus]: Math.round(average(highestResults)),
      })
    }
  }, [datapoints, state, speedtestStatus])

  const onProgress = useCallback(
    (speed: number) => {
      if (speedtestStatus === SPEED_TEST_STATUS.DOWNLOAD || speedtestStatus === SPEED_TEST_STATUS.UPLOAD) {
        if (isNaN(speed)) {
          setSpeedtestStatus(SPEED_TEST_STATUS.ERROR)
          return
        }

        const roundedSpeed = Math.round(speed / 1000000)
        setState({ ...state, [speedtestStatus]: roundedSpeed })
        datapoints[speedtestStatus].push(roundedSpeed)
      }
    },
    [datapoints, state, speedtestStatus, setSpeedtestStatus],
  )

  const onFinalize = useCallback(() => {
    normalizeResult()
    if (speedtestStatus === SPEED_TEST_STATUS.DOWNLOAD) {
      setSpeedtestStatus(SPEED_TEST_STATUS.UPLOAD)
      return
    }

    if (speedtestStatus === SPEED_TEST_STATUS.UPLOAD) {
      setSpeedtestStatus(SPEED_TEST_STATUS.PING)
    }
  }, [normalizeResult, speedtestStatus, setSpeedtestStatus])

  const runTest = useCallback(
    async (session: any) => {
      const params = {
        concurrency: concurrency > 1 ? concurrency : 10,
        duration: 10000,
        onfinalize: onFinalize,
        onprogress: onProgress,
        target: targetServerCollection,
        session,
      }

      try {
        await speedtest(params)
      } catch (error) {
        setSpeedtestStatus(SPEED_TEST_STATUS.ERROR)
      }
    },
    [concurrency, onFinalize, onProgress, targetServerCollection, setSpeedtestStatus],
  )

  const calculatePing = useCallback((result: number[]) => {
    const average = result.reduce((accumulator, currentValue) => accumulator + currentValue) / result.length
    return Math.round(average)
  }, [])

  const runLatencyTest = useCallback(async () => {
    if (!pingServerEndpoint) {
      setState({ ...state, [SPEED_TEST_STATUS.PING]: undefined })
      setSpeedtestStatus(SPEED_TEST_STATUS.COMPLETE)
      return
    }

    try {
      const result = await latencytest({ target: pingServerEndpoint })
      if (result) {
        const ping = calculatePing(result)
        setState({ ...state, [SPEED_TEST_STATUS.PING]: ping })
      }
      setSpeedtestStatus(SPEED_TEST_STATUS.COMPLETE)
    } catch (error) {
      setSpeedtestStatus(SPEED_TEST_STATUS.COMPLETE)
    }
  }, [calculatePing, pingServerEndpoint, state, setSpeedtestStatus])

  useEffect(() => {
    if (!isReady) return
    const statusChanged = prevTestStatus !== speedtestStatus
    if (statusChanged) {
      switch (speedtestStatus) {
        case SPEED_TEST_STATUS.DOWNLOAD:
          runTest(sessionDownload)
          break
        case SPEED_TEST_STATUS.UPLOAD:
          runTest(sessionUpload)
          break
        case SPEED_TEST_STATUS.PING:
          runLatencyTest()
          break
        case SPEED_TEST_STATUS.ERROR:
          resetSpeeds()
          break
        case SPEED_TEST_STATUS.COMPLETE:
          setTestRunning(false)
          break
      }
    }
    setPrevTestStatus(speedtestStatus)
  }, [isReady, prevTestStatus, resetSpeeds, runLatencyTest, runTest, state, speedtestStatus])

  const downloadSpeed = state[SPEED_TEST_STATUS.DOWNLOAD]
  const uploadSpeed = state[SPEED_TEST_STATUS.UPLOAD]
  const latency = state[SPEED_TEST_STATUS.PING]

  const returnPercentOfValue = (value: number) => {
    const convertToProcentage = (value / maxSpeed) * 100
    const limitValue = Math.min(Math.max(convertToProcentage, 0), 100)
    return limitValue
  }

  const styles = {
    root: {
      width: 'fit-content',
      maxWidth: '100%',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      margin: '0px auto',
      overflow: 'hidden',
    },
    gaugesContainer: {
      transition: `all 500ms ease`,
      display: 'flex',
      flexDirection: isMdDown ? 'column' : 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '20px',
      opacity: isAnimating ? '1' : '0',
      '& > *': {
        position: isAnimating ? 'relative ' : 'absolute ',

        transition: `all 500ms ease`,
      },
      '& .speedoMeter': {
        position: isAnimating ? 'relative ' : 'absolute',

        transition: `all 500ms ease`,
      },

      '& > *:first-child': {
        transform: isAnimating ? 'translateX(0)' : 'translateX(-200px)',
      },
      '& > *:last-child': {
        transform: isAnimating ? 'translateX(0)' : 'translateX(200px)',
      },
    },
    controls: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
    },
    visuals: {
      alignItems: 'center',
      '& svg': {
        height: 'auto',
      },
    },
    cta: {
      height: '50px',
      width: '200px',
      fontSize: '1.4rem',
      position: 'absolute',
      padding: '10px 20px',
      zIndex: 10,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '100%',
        height: '100%',
        zIndex: -1,
        backgroundColor: palette.primary.main,
        borderRadius: '4px',
        transform: 'translate(-50%, -50%) scale(1)',
        animation: 'beats 2s infinite',
      },
      '&:hover::before': {
        animation: 'none', // Stop the animation on hover
      },
      '@keyframes beats': {
        '0%': {
          transform: 'translate(-50%, -50%) scale(1)',
          opacity: 0.3,
        },
        '100%': {
          transform: 'translate(-50%, -50%) scale(1.5)',
          opacity: 0,
        },
      },
    },
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.gaugesContainer}>
        <Speedometer
          id="download-speedometer"
          {...(!isMobile ? { width: 300, height: 300 } : {})}
          gradientPosition="START"
          value={returnPercentOfValue(downloadSpeed ?? 0)}
        >
          {downloadSpeed !== 0 && (
            <SpeedometerTitle>
              {downloadSpeed}
              <Typography component="span" variant="subtitle1">
                Mbps
              </Typography>
            </SpeedometerTitle>
          )}
          <SpeedometerDisclaimer>{downloadLabel ? downloadLabel : 'Download'}</SpeedometerDisclaimer>
        </Speedometer>

        {!isMdUp && latency !== 0 && <PingElement pingValue={latency ?? 0} />}

        <Speedometer
          id="upload-speedometer"
          {...(!isMobile ? { width: 300, height: 300 } : {})}
          gradientPosition="START"
          value={returnPercentOfValue(uploadSpeed ?? 0)}
        >
          {uploadSpeed !== 0 && (
            <SpeedometerTitle>
              {uploadSpeed}
              <Typography component="span" variant="subtitle1">
                Mbps
              </Typography>
            </SpeedometerTitle>
          )}
          <SpeedometerDisclaimer>{uploadLabel ? uploadLabel : 'Upload'}</SpeedometerDisclaimer>
        </Speedometer>
      </Box>

      {isMdUp && latency !== 0 && <PingElement pingValue={latency ?? 0} />}

      {!testRunning && (
        <Box sx={{ ...styles.controls, ...(hasVisuals ? styles.visuals : {}) }}>
          {hasVisuals && speedtestStatus !== 'COMPLETE' && <SpeedtestBackground />}
          {speedtestStatus === 'COMPLETE' || speedtestStatus === 'ERROR' ? (
            <IconButton onClick={startSpeedtest}>
              <SvgIcon component={Refresh} viewBox="0 0 48 48" />
            </IconButton>
          ) : (
            <Button onClick={startSpeedtest} id={trackingId} variant="contained" sx={hasVisuals ? styles.cta : {}}>
              {buttonText}
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

export default Speedtest
